<template>
  <div class="tb">
    <header class="tb-head">
      <div class="tb-head__text">
        <h1 class="tb-title">Рейсы</h1>
        <p class="tb-lead">
          Что происходит, что горит и что делать. Время — местное время площадки, рядом — по Ульяновску.
          <router-link class="tb-link" :to="{ path: '/knowledge', query: { a: 'trips-list' } }">Как это работает</router-link>
        </p>
      </div>
      <div class="tb-head__actions">
        <button type="button" class="tb-btn tb-btn--ghost" :disabled="exporting || !total" @click="exportExcel">
          {{ exporting ? 'Готовим файл…' : 'Скачать Excel' }}
        </button>
        <router-link class="tb-btn tb-btn--ghost" to="/trips/classic" title="Старый список рейсов — на время перехода">Прежний вид</router-link>
        <button type="button" class="tb-btn" :disabled="pending" @click="reload()">
          <span :class="['tb-spin', { 'is-on': pending }]" aria-hidden="true">↻</span> Обновить
        </button>
      </div>
    </header>

    <div class="tb-scope" role="tablist" aria-label="Какие рейсы показывать">
      <button
        v-for="s in scopes" :key="s.key" type="button" role="tab" :aria-selected="state.scope === s.key"
        :class="['tb-seg', { 'is-active': state.scope === s.key }]" @click="change({ scope: s.key })"
      >{{ s.title }}</button>
    </div>

    <template v-if="state.scope === 'active'">
      <ProcessStory
        title="Рейсы сейчас"
        :flow="data && data.flow ? data.flow : null"
        :items="data?.items || []"
        :generated-at="freshAt"
        :failed="Boolean(error)"
        :active-light="state.light"
        :noun="NOUN"
        :money="money"
        :now="nowMs"
        @light="change({ light: $event })"
        @open="openById"
      />
      <ProcessFlow
        v-if="data && data.flow"
        title="Путь рейсов"
        :flow="data.flow"
        :items="data.items || []"
        :active="state.stage"
        :due-text="dueText"
        @stage="change({ stage: $event })"
        @open="openById"
      />
      <nav class="tb-views" aria-label="Быстрые виды">
        <button
          v-for="v in views" :key="v.key" type="button"
          :class="['tb-pill', `tb-pill--${v.key}`, { 'is-active': state.view === v.key }]"
          @click="change({ view: v.key })"
        >
          {{ v.title }}<b v-if="counts && counts[v.key] !== undefined" class="tb-pill__n">{{ counts[v.key] }}</b>
        </button>
      </nav>
    </template>

    <section class="tb-filters" aria-label="Поиск и отборы">
      <label class="tb-search">
        <span class="tb-sr">Поиск</span>
        <input
          v-model.trim="search" type="search" inputmode="search" autocomplete="off"
          placeholder="Номер рейса, город, получатель или ответственный"
          @keydown.enter="applySearch" @input="debouncedSearch"
        />
      </label>
      <select v-model="state.vehicle" aria-label="Тип ТС" @change="change({ vehicle: state.vehicle })">
        <option value="">Любой тип ТС</option>
        <option v-for="v in meta.vehicles || []" :key="v.key" :value="v.key">{{ v.title }}</option>
      </select>
      <select v-model="state.responsible" aria-label="Ответственный" @change="change({ responsible: state.responsible })">
        <option value="">Любой ответственный</option>
        <option value="me">Я</option>
        <option v-for="m in meta.managers || []" :key="m.id" :value="m.id">{{ m.name }}</option>
      </select>
      <select v-model="state.stage" aria-label="Этап" @change="change({ stage: state.stage })">
        <option value="">Любой этап</option>
        <option v-for="s in meta.stages || []" :key="s.key" :value="s.key">{{ s.title }}</option>
      </select>
      <label class="tb-date" title="Дата погрузки по местному времени площадки">
        <span>Погрузка с</span>
        <input v-model="state.load_from" type="date" @change="change({ load_from: state.load_from })" />
      </label>
      <label class="tb-date">
        <span>по</span>
        <input v-model="state.load_to" type="date" @change="change({ load_to: state.load_to })" />
      </label>
      <label class="tb-check">
        <input type="checkbox" :checked="state.top === '1'" @change="change({ top: $event.target.checked ? '1' : '' })" />
        Топ-8
      </label>
      <select v-model="state.sort" aria-label="Сортировка" @change="change({ sort: state.sort })">
        <option v-for="s in sorts" :key="s.key" :value="s.key">{{ s.title }}</option>
      </select>
      <button v-if="filtered" type="button" class="tb-btn tb-btn--reset" @click="resetAll">Сбросить</button>
    </section>

    <div v-if="chips.length" class="tb-chips" aria-label="Применённые отборы">
      <button v-for="c in chips" :key="c.key" type="button" class="tb-chip" @click="dropChip(c.key)">{{ c.text }} ✕</button>
    </div>

    <p v-for="(n, i) in (data?.notes || [])" :key="'n' + i" class="tb-note" role="status">{{ n }}</p>
    <p v-if="data?.truncated" class="tb-note tb-note--warn" role="status">
      Активных рейсов больше, чем помещается в расчёт сводки. Сузьте отбор — иначе часть рейсов не попадёт в светофор.
    </p>
    <p v-if="data && data.intake && data.intake.state && !['ok', 'unknown'].includes(data.intake.state)" class="tb-note tb-note--warn" role="alert">
      {{ data.intake.title }}{{ data.intake.text ? ': ' + data.intake.text : '' }}
    </p>
    <p v-if="error" class="tb-note tb-note--error" role="alert">
      {{ error }}<span v-if="data"> Показаны данные на {{ timeOf(data.generated_at) }}.</span>
      <button type="button" class="tb-link-btn" @click="reload()">Повторить</button>
    </p>

    <div v-if="!data && pending" class="tb-skeleton" aria-busy="true" aria-label="Загружаем рейсы">
      <div v-for="i in 6" :key="i" class="tb-skel-row"></div>
    </div>

    <p v-else-if="data && !rows.length" class="tb-empty">{{ emptyText(state) }}
      <button v-if="filtered" type="button" class="tb-link-btn" @click="resetAll">Сбросить отборы</button>
    </p>

    <TransitionGroup v-else tag="ul" name="tb-row" class="tb-list" aria-label="Список рейсов">
      <li v-for="(r, i) in rows" :key="r.pk" :style="{ '--i': Math.min(i, 12) }">
        <article
          :class="['tb-row', `tb-row--${r.light}`, { 'is-incomplete': r.incomplete }]"
          tabindex="0" role="link" :aria-label="`Рейс ${r.number || 'без номера'}, ${r.title}, ${r.light_title}`"
          @click="open(r)" @keydown.enter.prevent="open(r)" @keydown.space.prevent="open(r)"
        >
          <div class="tb-row__state">
            <span :class="['tb-light', `tb-light--${r.light}`]" aria-hidden="true"></span>
            <span class="tb-row__light">{{ r.light_title }}</span>
          </div>
          <div class="tb-row__main">
            <div class="tb-row__top">
              <b class="tb-row__num">{{ r.number || 'Без номера' }}</b>
              <span v-if="r.is_top" class="tb-top" title="Топ-клиент">Топ-8</span>
              <span class="tb-stage">{{ r.stage_title }}</span>
              <span v-if="r.status && r.status !== r.stage_title" class="tb-muted">· {{ r.status }}</span>
              <span v-if="r.outcome_title" class="tb-outcome">{{ r.outcome_title }}</span>
            </div>
            <div class="tb-row__route">{{ r.title }}<span v-if="r.vehicle_title" class="tb-muted"> · {{ r.vehicle_title }}</span></div>
            <div v-if="r.client" class="tb-row__client">{{ r.client }}</div>
          </div>
          <div class="tb-row__times">
            <span><em>Погрузка</em>{{ placeTime(r.load_at, r.load_tz) }}</span>
            <span><em>Выгрузка</em>{{ placeTime(r.unload_at, r.unload_tz) }}</span>
            <span v-if="dueText(r)" :class="['tb-due', { 'is-late': r.minutes_left < 0 }]">{{ dueText(r) }}</span>
          </div>
          <div class="tb-row__act">
            <span class="tb-who">{{ r.responsible || 'Без ответственного' }}</span>
            <span v-if="r.next_step" class="tb-next">{{ r.next_step }}</span>
            <span v-if="r.inwork_left_min !== null && r.inwork_left_min !== undefined" class="tb-timer">{{ inworkText(r.inwork_left_min) }}</span>
            <span v-if="r.flags.length" class="tb-flags">
              <span v-for="f in r.flags" :key="f.key" :class="['tb-flag', `tb-flag--${f.level}`]">{{ f.title }}</span>
            </span>
            <span v-if="moneyLine(r)" class="tb-money">{{ moneyLine(r) }}</span>
          </div>
        </article>
      </li>
    </TransitionGroup>

    <footer v-if="rows.length" class="tb-foot">
      <span class="tb-muted">{{ shownText(rows.length, total) }}</span>
      <button v-if="data?.has_more" type="button" class="tb-btn" :disabled="pending" @click="loadMore">
        {{ pending ? 'Загружаем…' : 'Показать ещё' }}
      </button>
    </footer>
  </div>
</template>

<script setup>
// Модуль «Рейсы» (решение Анны 04.11.2026): экран по стандарту интерфейса docs/main 39. [Claude]
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ProcessStory from '@/components/process/ProcessStory.vue'
import ProcessFlow from '@/components/process/ProcessFlow.vue'
import { downloadTripsExcel, fetchTripsBoard, fetchTripsMeta } from './tripsApi.js'
import {
  NOUN, activeChips, apiParams, clearChip, dueText, emptyText, inworkText, isFiltered, money, moneyLine, placeTime,
  queryFromState, shownText, stateFromQuery, withChange,
} from './tripsBoardLogic.mjs'

const REFRESH_MS = 60 * 1000

const route = useRoute()
const router = useRouter()
const state = reactive(stateFromQuery(route.query))
const search = ref(state.q)
const data = ref(null)
const rows = ref([])
const meta = ref({})
const pending = ref(false)
const exporting = ref(false)
const error = ref('')
const nowMs = ref(Date.now())
let controller = null
let timer = null
let searchTimer = null

const scopes = [{ key: 'active', title: 'Активные' }, { key: 'all', title: 'Все рейсы' }]
const views = computed(() => meta.value.views || [
  { key: 'all', title: 'Все активные' }, { key: 'burning', title: 'Горит' }, { key: 'mine', title: 'Мои' },
  { key: 'need_executor', title: 'Без исполнителя' }, { key: 'load_today', title: 'Погрузка сегодня' },
  { key: 'in_transit', title: 'В пути' },
])
const sorts = computed(() => (meta.value.sorts || [
  { key: 'urgency', title: 'Сначала срочные' }, { key: 'load', title: 'По погрузке: ближайшие' },
  { key: 'load_desc', title: 'По погрузке: последние' },
]).filter((s) => state.scope === 'active' || s.key !== 'urgency'))
const counts = computed(() => data.value?.counts || null)
const total = computed(() => data.value?.total || 0)
const filtered = computed(() => isFiltered(state))
const chips = computed(() => activeChips(state, meta.value))
const freshAt = computed(() => data.value?.data_as_of || data.value?.generated_at || '')

function timeOf(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
}

async function load({ append = false, silent = false } = {}) {
  if (controller) controller.abort()
  controller = new AbortController()
  const offset = append ? rows.value.length : 0
  const limit = silent && rows.value.length > 0 ? Math.min(Math.max(rows.value.length, 50), 200) : undefined
  if (!silent) pending.value = true
  try {
    const result = await fetchTripsBoard(apiParams(state, { offset, limit }), { signal: controller.signal })
    data.value = result
    rows.value = append ? rows.value.concat(result.items || []) : (result.items || [])
    error.value = ''
  } catch (e) {
    if (e?.name === 'CanceledError' || e?.name === 'AbortError' || e?.code === 'ERR_CANCELED') return
    error.value = e?.message ? `Не удалось обновить список: ${e.message}.` : 'Не удалось обновить список.'
  } finally {
    if (!silent) pending.value = false
    nowMs.value = Date.now()
  }
}

function reload() { return load() }
function loadMore() { return load({ append: true }) }

function syncUrl() {
  router.replace({ query: queryFromState(state) }).catch(() => {})
}

function change(patch) {
  Object.assign(state, withChange(state, patch))
  syncUrl()
  load()
}

function applySearch() {
  clearTimeout(searchTimer)
  if (search.value === state.q) return
  change({ q: search.value })
}

function debouncedSearch() {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(applySearch, 450)
}

function resetAll() {
  search.value = ''
  Object.assign(state, stateFromQuery({ scope: state.scope }))
  syncUrl()
  load()
}

function dropChip(key) {
  Object.assign(state, clearChip(state, key))
  syncUrl()
  load()
}

function open(row) {
  if (row?.pk) router.push(`/trips/${row.pk}`)
}

function openById(id) {
  open((data.value?.items || []).concat(rows.value).find((r) => r.id === id))
}

async function exportExcel() {
  exporting.value = true
  try {
    await downloadTripsExcel(apiParams(state))
  } catch (e) {
    error.value = e?.message ? `Excel не сформирован: ${e.message}` : 'Excel не сформирован.'
  } finally {
    exporting.value = false
  }
}

// «Назад»/«Вперёд» в браузере: адрес изменился не нами — перечитываем отборы.
watch(() => route.query, (query) => {
  if (route.path !== '/trips') return
  const next = stateFromQuery(query)
  if (JSON.stringify(queryFromState(next)) === JSON.stringify(queryFromState(state))) return
  Object.assign(state, next)
  search.value = state.q
  load()
})

function tick() {
  nowMs.value = Date.now()
  if (document.visibilityState === 'visible' && !pending.value) load({ silent: true })
}

onMounted(async () => {
  load()
  try { meta.value = await fetchTripsMeta() } catch { meta.value = {} }
  timer = setInterval(tick, REFRESH_MS)
})

onBeforeUnmount(() => {
  clearInterval(timer)
  clearTimeout(searchTimer)
  if (controller) controller.abort()
})
</script>

<style scoped>
.tb { --gold: #f2b705; --gold-2: #ffe08a; --ink: #1f2430; --muted: #6b7280; --line: #ebe7dc; --card: #fff;
  --red: #dc2626; --yellow: #d97706; --green: #16a34a; --grey: #9ca3af;
  max-width: 1280px; margin: 0 auto; padding: 24px 20px 80px; display: flex; flex-direction: column; gap: 14px;
  color: var(--ink); background: transparent; }
.tb-head { display: flex; justify-content: space-between; align-items: flex-end; gap: 16px; flex-wrap: wrap;
  animation: tb-in .45s cubic-bezier(.2, .7, .2, 1) both; }
.tb-title { margin: 0; font-size: 30px; font-weight: 800; letter-spacing: -.02em; }
.tb-lead { margin: 6px 0 0; color: var(--muted); font-size: 14px; max-width: 720px; }
.tb-link, .tb-link-btn { color: #8a6d00; font-weight: 600; text-decoration: none; border: 0; background: none; cursor: pointer; font: inherit; padding: 0 4px; }
.tb-link:hover, .tb-link-btn:hover { text-decoration: underline; }
.tb-head__actions { display: flex; gap: 8px; flex-wrap: wrap; }
.tb-btn { display: inline-flex; align-items: center; gap: 6px; padding: 9px 16px; border-radius: 999px; border: 0; font: inherit;
  font-weight: 700; font-size: 13.5px; cursor: pointer; color: var(--ink); text-decoration: none;
  background: linear-gradient(135deg, var(--gold-2), var(--gold)); transition: transform .2s, box-shadow .2s, opacity .2s; }
.tb-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 10px 22px -12px rgba(242, 183, 5, .95); }
.tb-btn:disabled { opacity: .55; cursor: default; }
.tb-btn--ghost { background: #fff; border: 1px solid var(--line); }
.tb-btn--reset { background: #fff; border: 1px solid rgba(220, 38, 38, .35); color: #991b1b; }
.tb-spin { display: inline-block; } .tb-spin.is-on { animation: tb-rot 1s linear infinite; }
.tb-scope { display: inline-flex; align-self: flex-start; padding: 4px; gap: 4px; border-radius: 999px; background: #f4f1e8; border: 1px solid var(--line); }
.tb-seg { border: 0; background: transparent; padding: 7px 16px; border-radius: 999px; font: inherit; font-weight: 600; font-size: 13.5px; cursor: pointer; color: var(--muted); transition: background .2s, color .2s; }
.tb-seg.is-active { background: #fff; color: var(--ink); box-shadow: 0 6px 16px -12px rgba(31, 36, 48, .6); }
.tb-views { display: flex; gap: 8px; flex-wrap: wrap; }
.tb-pill { display: inline-flex; align-items: center; gap: 8px; padding: 7px 14px; border-radius: 999px; border: 1px solid var(--line);
  background: #fff; font: inherit; font-size: 13.5px; font-weight: 600; cursor: pointer; transition: border-color .2s, box-shadow .2s, transform .2s; }
.tb-pill:hover { transform: translateY(-1px); }
.tb-pill.is-active { border-color: var(--gold); box-shadow: 0 0 0 3px rgba(242, 183, 5, .2); }
.tb-pill__n { min-width: 22px; padding: 1px 7px; border-radius: 999px; background: #f4f1e8; font-size: 12px; text-align: center; }
.tb-pill--burning .tb-pill__n { background: #fee2e2; color: var(--red); }
.tb-filters { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; padding: 12px; border-radius: 16px; background: #fbfaf6; border: 1px solid var(--line); }
.tb-search { flex: 1 1 280px; }
.tb-search input, .tb-filters select, .tb-date input { width: 100%; min-height: 38px; padding: 0 12px; border-radius: 12px; border: 1px solid var(--line);
  background: #fff; font: inherit; font-size: 14px; color: var(--ink); outline: none; transition: border-color .2s, box-shadow .2s; }
.tb-filters select { width: auto; max-width: 220px; }
.tb-search input:focus, .tb-filters select:focus, .tb-date input:focus { border-color: var(--gold); box-shadow: 0 0 0 3px rgba(242, 183, 5, .2); }
.tb-date { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; color: var(--muted); }
.tb-date input { width: 150px; }
.tb-check { display: inline-flex; align-items: center; gap: 6px; font-size: 13.5px; cursor: pointer; }
.tb-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); }
.tb-chips { display: flex; gap: 6px; flex-wrap: wrap; }
.tb-chip { padding: 4px 10px; border-radius: 999px; border: 1px solid var(--gold); background: #fff8e1; font: inherit; font-size: 12.5px; cursor: pointer; }
.tb-note { margin: 0; padding: 8px 12px; border-radius: 12px; background: #f4f1e8; font-size: 13px; color: #374151; }
.tb-note--warn { background: #fff7ed; color: #9a3412; }
.tb-note--error { background: #fef2f2; color: #991b1b; }
.tb-empty { margin: 0; padding: 28px; text-align: center; color: var(--muted); border-radius: 16px; border: 1px dashed var(--line); background: #fff; }
.tb-skeleton { display: grid; gap: 8px; }
.tb-skel-row { height: 84px; border-radius: 16px; background: linear-gradient(90deg, #f4f1e8 25%, #fbfaf6 50%, #f4f1e8 75%);
  background-size: 200% 100%; animation: tb-shimmer 1.2s infinite linear; }
.tb-list { list-style: none; margin: 0; padding: 0; display: grid; gap: 8px; }
.tb-list > li { animation: tb-rise .4s cubic-bezier(.2, .7, .2, 1) both; animation-delay: calc(var(--i) * 30ms); }
.tb-row { position: relative; display: grid; grid-template-columns: 120px minmax(0, 1.4fr) minmax(0, 1.1fr) minmax(0, 1.4fr); gap: 14px;
  align-items: start; padding: 12px 14px 12px 18px; border-radius: 16px; background: var(--card); border: 1px solid var(--line);
  cursor: pointer; transition: transform .2s, box-shadow .2s, border-color .2s; }
.tb-row::before { content: ''; position: absolute; inset: 8px auto 8px 0; width: 4px; border-radius: 0 4px 4px 0; background: var(--grey); }
.tb-row--red::before { background: var(--red); } .tb-row--yellow::before { background: var(--yellow); }
.tb-row--green::before { background: var(--green); } .tb-row--done { opacity: .75; } .tb-row--done::before { background: #d6d3cb; }
.tb-row--red { border-color: rgba(220, 38, 38, .3); animation: tb-alert 2.6s ease-in-out infinite; }
.tb-row:hover { transform: translateY(-1px); box-shadow: 0 14px 26px -22px rgba(31, 36, 48, .7); }
.tb-row:focus-visible { outline: 2px solid var(--gold); outline-offset: 2px; }
.tb-row.is-incomplete { border-style: dashed; }
.tb-row__state { display: flex; flex-direction: column; gap: 6px; }
.tb-light { width: 12px; height: 12px; border-radius: 50%; background: var(--grey); }
.tb-light--red { background: var(--red); box-shadow: 0 0 0 4px rgba(220, 38, 38, .15); } .tb-light--yellow { background: var(--yellow); }
.tb-light--green { background: var(--green); } .tb-light--done { background: #d6d3cb; }
.tb-row__light { font-size: 12.5px; font-weight: 700; line-height: 1.3; }
.tb-row--red .tb-row__light { color: var(--red); } .tb-row--yellow .tb-row__light { color: #b45309; }
.tb-row__top { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.tb-row__num { font-size: 16px; font-weight: 800; letter-spacing: -.01em; }
.tb-top { padding: 1px 8px; border-radius: 999px; background: #fff3c4; color: #8a6d00; font-size: 11.5px; font-weight: 700; }
.tb-stage { padding: 2px 9px; border-radius: 999px; background: #f4f1e8; font-size: 12px; font-weight: 600; }
.tb-outcome { padding: 2px 9px; border-radius: 999px; background: #f3f4f6; font-size: 12px; }
.tb-row__route { margin-top: 4px; font-size: 15px; font-weight: 600; overflow-wrap: anywhere; }
.tb-row__client { font-size: 12.5px; color: var(--muted); overflow-wrap: anywhere; }
.tb-row__times { display: grid; gap: 3px; font-size: 13px; }
.tb-row__times em { display: inline-block; min-width: 72px; font-style: normal; color: var(--muted); font-size: 12px; }
.tb-due { font-weight: 600; } .tb-due.is-late { color: var(--red); }
.tb-row__act { display: flex; flex-direction: column; gap: 4px; font-size: 13px; }
.tb-who { color: #374151; }
.tb-next { padding: 6px 9px; border-radius: 10px; background: #fff8e1; font-weight: 600; line-height: 1.35; }
.tb-timer { color: #8a6d00; font-weight: 600; }
.tb-flags { display: flex; flex-wrap: wrap; gap: 4px; }
.tb-flag { padding: 1px 8px; border-radius: 999px; font-size: 11.5px; background: #f3f4f6; }
.tb-flag--critical { background: #fee2e2; color: #991b1b; } .tb-flag--warning { background: #fff7ed; color: #9a3412; }
.tb-money { color: var(--muted); font-size: 12.5px; }
.tb-muted { color: var(--muted); }
.tb-foot { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; }
.tb-row-move { transition: transform .35s cubic-bezier(.2, .7, .2, 1); }
.tb-row-enter-active, .tb-row-leave-active { transition: opacity .25s, transform .25s; }
.tb-row-enter-from, .tb-row-leave-to { opacity: 0; transform: translateY(8px); }
@keyframes tb-in { from { opacity: 0; transform: translateY(8px); } }
@keyframes tb-rise { from { opacity: 0; transform: translateY(8px); } }
@keyframes tb-rot { to { transform: rotate(360deg); } }
@keyframes tb-shimmer { to { background-position: -200% 0; } }
@keyframes tb-alert { 50% { box-shadow: 0 0 0 5px rgba(220, 38, 38, .06); } }
@media (max-width: 960px) {
  .tb-row { grid-template-columns: 100px minmax(0, 1fr); }
  .tb-row__times, .tb-row__act { grid-column: 2; }
}
@media (max-width: 640px) {
  .tb { padding: 16px 12px 80px; }
  .tb-title { font-size: 24px; }
  .tb-row { grid-template-columns: minmax(0, 1fr); gap: 8px; }
  .tb-row__state { flex-direction: row; align-items: center; }
  .tb-row__times, .tb-row__act { grid-column: 1; }
  .tb-filters select, .tb-date, .tb-date input { width: 100%; max-width: none; }
  .tb-head__actions { width: 100%; }
}
@media (prefers-reduced-motion: reduce) {
  .tb-head, .tb-list > li, .tb-row--red, .tb-skel-row, .tb-spin.is-on { animation: none; }
  .tb-row-move, .tb-row-enter-active, .tb-row-leave-active { transition: none; }
}
</style>
